import React from "react";

import { Box, Button, Paper, Portal, Stack, Typography } from "@mui/material";

const PAD = 10;

/**
 * Lightweight first-visit spotlight tour (no extra deps).
 * Each step: { selector, title, body }
 */
export default function MenuTour({ open, steps, LANG, onComplete }) {
    const [index, setIndex] = React.useState(0);
    const [rect, setRect] = React.useState(null);
    const [cardPos, setCardPos] = React.useState({ top: 24, left: 16 });
    const [activeSteps, setActiveSteps] = React.useState([]);

    const step = activeSteps[index] || null;
    const total = activeSteps.length;

    const finish = React.useCallback(() => {
        onComplete?.();
    }, [onComplete]);

    React.useEffect(() => {
        if (!open) {
            setIndex(0);
            setActiveSteps([]);
            return undefined;
        }

        let attempts = 0;
        let timer = null;
        let cancelled = false;
        const resolve = () => {
            if (cancelled) return;
            const found = (steps || []).filter((s) => s?.selector && document.querySelector(s.selector));
            if (found.length) {
                setActiveSteps(found);
                return;
            }
            attempts += 1;
            if (attempts < 12) {
                timer = window.setTimeout(resolve, 100);
            } else {
                finish();
            }
        };
        timer = window.setTimeout(resolve, 50);
        return () => {
            cancelled = true;
            if (timer) window.clearTimeout(timer);
        };
    }, [open, steps, finish]);

    const measure = React.useCallback(() => {
        if (!step?.selector) {
            setRect(null);
            return;
        }
        const el = document.querySelector(step.selector);
        if (!el) {
            setRect(null);
            return;
        }
        el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
        const next = el.getBoundingClientRect();
        setRect(next);

        const cardWidth = Math.min(320, window.innerWidth - 32);
        let top = next.bottom + 12;
        let left = Math.min(Math.max(16, next.left), window.innerWidth - cardWidth - 16);
        if (top + 180 > window.innerHeight) {
            top = Math.max(16, next.top - 188);
        }
        setCardPos({ top, left });
    }, [step]);

    React.useEffect(() => {
        if (!open || !total) return;
        if (index >= total) setIndex(0);
    }, [open, total, index]);

    React.useEffect(() => {
        if (!open || !step) return undefined;
        const t = window.setTimeout(measure, 80);
        window.addEventListener("resize", measure);
        window.addEventListener("scroll", measure, true);
        return () => {
            window.clearTimeout(t);
            window.removeEventListener("resize", measure);
            window.removeEventListener("scroll", measure, true);
        };
    }, [open, step, measure, index]);

    if (!open || !step || !total) return null;

    const hole = rect
        ? {
              top: Math.max(0, rect.top - PAD),
              left: Math.max(0, rect.left - PAD),
              width: rect.width + PAD * 2,
              height: rect.height + PAD * 2
          }
        : null;

    const goNext = () => {
        if (index >= total - 1) {
            finish();
            return;
        }
        setIndex((i) => i + 1);
    };

    return (
        <Portal>
            <Box
                role="dialog"
                aria-modal="true"
                aria-label={step.title}
                sx={{ position: "fixed", inset: 0, zIndex: (theme) => theme.zIndex.modal + 2 }}>
                {/* Dim overlay with spotlight cutout via box-shadow */}
                {hole ? (
                    <Box
                        onClick={finish}
                        sx={{
                            position: "fixed",
                            top: hole.top,
                            left: hole.left,
                            width: hole.width,
                            height: hole.height,
                            borderRadius: 2,
                            boxShadow: "0 0 0 9999px rgba(15, 20, 28, 0.55)",
                            pointerEvents: "none",
                            transition: "top 0.2s ease, left 0.2s ease, width 0.2s ease, height 0.2s ease"
                        }}
                    />
                ) : (
                    <Box onClick={finish} sx={{ position: "fixed", inset: 0, bgcolor: "rgba(15, 20, 28, 0.55)" }} />
                )}

                <Paper
                    elevation={8}
                    sx={{
                        position: "fixed",
                        top: cardPos.top,
                        left: cardPos.left,
                        width: { xs: "calc(100vw - 32px)", sm: 320 },
                        maxWidth: 320,
                        p: 2,
                        borderRadius: 2,
                        zIndex: 1
                    }}>
                    <Stack spacing={1.5}>
                        <Typography variant="caption" color="text.secondary">
                            {LANG.MENU_TOUR_STEP(index + 1, total)}
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontFamily: "Fraunces, serif" }}>
                            {step.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {step.body}
                        </Typography>
                        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                            <Button size="small" color="inherit" onClick={finish}>
                                {LANG.MENU_TOUR_SKIP}
                            </Button>
                            <Button size="small" variant="contained" onClick={goNext}>
                                {index >= total - 1 ? LANG.MENU_TOUR_DONE : LANG.MENU_TOUR_NEXT}
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>
            </Box>
        </Portal>
    );
}
